import { useEffect, useState, useContext, useCallback, useRef } from "react";
import { getAllSubscription } from "api/graphQL_subscriptions";
import { getAllGatewaysSecond } from "api/graphQL_gateways";
import SubscriptionList from "./components/subscriptionlist";
import { ClientContext } from "ClientProvider";
import DivLoader from "components/divloader/DivLoader";

export type SubscriptionObj = {
  start_date: string;
  interval: string;
  managed_by: any;
  duration: number;
  amount: number;
  gateway_hash: string;
  currency: string;
  gateway_name: string;
  descriptor: string;
  paid_payments: number;
  transaction: string;
  billing_cycles: number;
  status: string;
  id: string;
  client: string;
  client_id: string;
  client_secret: string;
  subscription_id: string;
  subscription_provider: string;
  subscription_provider_name: string;
  payment: any;
  created_at: any;
  next_payment_date: any;
};

const Subscription = () => {
  const [subscriptions, setSubscriptions] = useState<SubscriptionObj[]>([]);
  const [isListLoading, setIsListLoading] = useState<boolean>();
  const [page, setPage] = useState<number>(1);
  const [totalpage, setTotalPage] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [statusChanged, setStatusChanged] = useState(false);
  const [pageSize, setPageSize] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchTexts, setSearchTexts] = useState<string>("");
  const [allGateways, setAllGateways] = useState<any[]>([]);
  const { singleRoleData } = useContext(ClientContext);
  const [isRoleDataLoaded, setIsRoleDataLoaded] = useState(false);
  const firstRender = useRef(true);
  const isResetting = useRef(false);
  const [clearClicked, setClearClick] = useState(0);

  const [filter, setFilter] = useState({
    clients: [],
    interval: [],
    duration: null,
    status: [],
  });

  useEffect(() => {
    if (Array.isArray(singleRoleData) && singleRoleData.length > 0) {
      setIsRoleDataLoaded(true);
    }
  }, [singleRoleData]);

  const fetchSubscriptions = useCallback(() => {
    setIsListLoading(true);
    if (pageSize === null || pageSize === undefined) { setIsListLoading(false); return; }
    getAllSubscription({ page, per_page: pageSize, searchTexts, filter })
      .then((data) => {
        setSubscriptions(data?.subscriptions ?? []);
        setTotalPage(data.total_pages);
        setTotalItems(data.total_items);
        setCurrentPage(data.current_page);
      })
      .catch(() => setSubscriptions([]))
      .finally(() => setIsListLoading(false));
  }, [page, pageSize, searchTexts]);

  const fetchAllGateways = useCallback(() => {
    getAllGatewaysSecond().then((data) => setAllGateways(data)).catch(() => {});
  }, []);

  useEffect(() => { fetchAllGateways(); }, [fetchAllGateways]);

  const serchboxValueChange = async (searchTerm: string) => {
    const search_txt = searchTerm || "";
    if (search_txt !== searchTexts && searchTexts !== "") {
      await setSearchTexts(search_txt); setPage(1); setCurrentPage(1);
    } else {
      await setSearchTexts(search_txt);
    }
  };

  const filterData = () => {
    setIsListLoading(true);
    if (pageSize === null || pageSize === undefined) { setIsListLoading(false); return; }
    getAllSubscription({ page, per_page: pageSize, searchTexts, filter })
      .then((data) => {
        setSubscriptions(data?.subscriptions ?? []);
        setTotalPage(data.total_pages);
        setTotalItems(data.total_items);
        setCurrentPage(data.current_page);
      })
      .catch(() => setSubscriptions([]))
      .finally(() => setIsListLoading(false));
  };

  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (isResetting.current) { isResetting.current = false; return; }
    fetchSubscriptions();
  }, [searchTexts, page, pageSize, statusChanged, isRoleDataLoaded, fetchSubscriptions]);

  useEffect(() => { onClearClick(); }, []);
  useEffect(() => { setPageSize(50); }, [searchTexts]);

  const onClearClick = () => {
    setFilter({
      clients: [],
      interval: [],
      duration: null,
      status: [],
    });

    setPage(1);
    setPageSize(50);
    setCurrentPage(1);
  };

  return (
    <div className="sub-root">
      {/* Page header */}
      <div className="sub-page-header">
        <div className="sub-title-block">
          <div className="sub-page-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
              <line x1="12" y1="22.08" x2="12" y2="12"/>
            </svg>
          </div>
          <div>
            <h1 className="sub-heading">Subscriptions</h1>
            <p className="sub-sub">Manage recurring billing and subscription plans</p>
          </div>
        </div>
      </div>

      {isRoleDataLoaded && allGateways.length > 0 ? (
        <SubscriptionList
          pageSize={pageSize}
          setPageSize={setPageSize}
          data={subscriptions}
          isLoading={isListLoading}
          page={page}
          setPage={setPage}
          totalpage={totalpage}
          totalItems={totalItems}
          currentPage={currentPage}
          onValueChange={serchboxValueChange}
          roleData={singleRoleData}
          allGateways={allGateways}
          fetchSubscriptions={fetchSubscriptions}
          filterData={filterData}
          filter={filter}
          setFilter={setFilter}
          setClearClick={onClearClick}
          setStatusChanged={setStatusChanged}
          setIsLoading={setIsListLoading}
          searchTexts={searchTexts}
        />
      ) : (
        <div className="sub-loading">
          <div className="sub-spinner" />
          <p className="sub-loading-txt">
            Loading required data for the subscription...
          </p>
        </div>
      )}  

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .sub-root { font-family:'Plus Jakarta Sans',sans-serif; display:flex; flex-direction:column; gap:20px; }
        .sub-page-header { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:16px; }
        .sub-title-block { display:flex; align-items:center; gap:14px; }
        .sub-page-icon { width:44px; height:44px; border-radius:12px; background:linear-gradient(135deg,#eff6ff,#dbeafe); border:1.5px solid #bfdbfe; display:flex; align-items:center; justify-content:center; color:#2563eb; flex-shrink:0; }
        .sub-heading { font-size:20px; font-weight:800; color:#0f172a; letter-spacing:-0.4px; margin-bottom:2px; line-height:1.2; }
        .sub-sub { font-size:13px; color:#94a3b8; font-weight:500; }
        .sub-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:60vh; gap:14px; }
        .sub-spinner { width:30px; height:30px; border:3px solid #e2e8f0; border-top-color:#2563eb; border-radius:50%; animation:subSpin .7s linear infinite; }
        @keyframes subSpin { to{transform:rotate(360deg)} }
        .sub-loading-txt { font-size:13px; color:#94a3b8; font-weight:500; }
      `}</style>
    </div>
  );
};

export default Subscription;